import React, { FC } from 'react';
import { memo, useMemo } from 'react';

import FeatureOption from './FeatureOption';
import {
  Checkbox,
  FeatureContainer,
  FeatureDropdownContainer,
  FeatureOptionsContainer,
  FeatureTitle,
  LabelsContainer,
  OptionContainer,
} from './Labels.style';
import PhaseOption from './PhaseOption';
import FeatureDropdown from './FeatureDropdown';
import { useNavigate, useParams } from 'react-router-dom';
import { labelToColor } from '../../../../../utilities/labelColors';

interface IProps {
  segmentationClasses: any;
  activeSegmentationClass: any;
  activeFrame: any;
  dicom: any;
  phaseLabels: any;
  segmentations: any;
  segmentationReferences: any;
  setActiveFrameIndex: any;
  setActiveSegmentationClass: any;
  setSegmentation: any;
  study: any;
  activeOtherSegmentationClasses: any;
  setActiveOtherSegmentationClasses: any;
}

const Labels: FC<IProps> = props => {
  const {
    segmentationClasses,
    activeSegmentationClass,
    activeFrame,
    dicom,
    phaseLabels,
    segmentations,
    segmentationReferences,
    setActiveFrameIndex,
    setActiveSegmentationClass,
    setSegmentation,
    study,
    activeOtherSegmentationClasses,
    setActiveOtherSegmentationClasses,
  } = props;
  const navigate = useNavigate();
  const { studyIcid, dicomIcid } = useParams();

  const usablePhases = phaseLabels?.filter(phaseLabel => phaseLabel?.phaseClass !== 'Mid-Systole');
  const phaseLabelOptions = usablePhases?.map((phaseLabel, index) => {
    const isActive = activeFrame?.uuid === phaseLabel?.frame;
    const segmentationClassUuids = segmentationClasses?.map(
      segmentationClass => segmentationClass?.uuid
    );
    const labeledSegmentations = segmentations?.filter(
      segmentation =>
        segmentation?.frame === phaseLabel?.frame &&
        segmentationClassUuids?.includes(segmentation?.segmentationClass) &&
        (!!segmentation?.paths?.length || !segmentation?.isFullyVisible)
    );
    const isLabeled = labeledSegmentations?.length === segmentationClasses?.length;
    const handlePhaseOptionOnClick = () => {
      setActiveFrameIndex(phaseLabel?.frameIndex);
      navigate(`/${studyIcid}/${dicomIcid}/${phaseLabel?.frameIcid}`, { replace: true });
    };

    return (
      <PhaseOption
        key={index}
        phaseLabel={phaseLabel}
        isActive={isActive}
        isLabeled={isLabeled}
        handlePhaseOptionOnClick={handlePhaseOptionOnClick}
      />
    );
  });

  const segmentationClassOptions = useMemo(
    () =>
      segmentationClasses?.map((segmentationClass, index) => {
        const segmentation = segmentations?.find(
          segmentation =>
            segmentation?.segmentationClass === segmentationClass?.uuid &&
            segmentation?.frame === activeFrame?.uuid
        );
        const isActive = activeSegmentationClass?.uuid === segmentationClass?.uuid;
        const isLabeled = !!segmentation?.paths?.length || segmentation?.isFullyVisible === false;

        const setActive = () => setActiveSegmentationClass(segmentationClass);
        return (
          <FeatureOption
            key={segmentationClass?.uuid}
            segmentationClass={segmentationClass?.value}
            hotkey={`${index + 1}`}
            isActive={isActive}
            isLabeled={isLabeled}
            setActive={setActive}
          />
        );
      }),
    [
      activeSegmentationClass?.uuid,
      activeFrame?.uuid,
      segmentationClasses,
      segmentations,
      setActiveSegmentationClass,
    ]
  );

  const visibilityHotkeys = ['J', 'K', 'N', 'M'];

  const filteredVisibilityOptions = study?.isIceStudy
    ? // only show visibility for the selected feature for ICE studies
      segmentationClasses.filter(
        segmentationClass => segmentationClass.uuid === activeSegmentationClass?.uuid
      )
    : segmentationClasses; // show all options if not an ICE study

  const visibilityOptions = filteredVisibilityOptions?.map((segmentationClass, index) => {
    const segmentationLabel = segmentations?.find(
      segmentation =>
        segmentation?.segmentationClass === segmentationClass?.uuid &&
        segmentation?.frame === activeFrame?.uuid
    );

    const isFullyVisible = segmentationLabel ? segmentationLabel?.isFullyVisible : true;
    const handleVisibilityOptionOnClick = () => {
      setSegmentation({
        ...segmentationLabel,
        dicom: dicom?.uuid,
        segmentationClass: segmentationClass?.uuid,
        frame: activeFrame?.uuid,
        frameIndex: activeFrame?.index,
        isFullyVisible: !isFullyVisible,
      });
    };

    return (
      <OptionContainer key={index} onClick={handleVisibilityOptionOnClick}>
        <Checkbox checked={isFullyVisible} readOnly />({visibilityHotkeys[index]}){' '}
        {segmentationClass?.value} is Fully Visible
      </OptionContainer>
    );
  });

  // unique set of labeled classes
  const labeledClasses = (segmentations
    ?.filter((segmentation: any) => !!segmentation?.paths?.length)
    ?.map((segmentation: any) => segmentation?.segmentationClassValue) || []) as string[];
  const uniqueLabeledClasses = Array.from(new Set(labeledClasses)) as string[];

  const otherSegmentationClassOptions = uniqueLabeledClasses.map((segmentationClass: string) => {
    const isActive = activeOtherSegmentationClasses.includes(segmentationClass);

    const handleCheckboxClick = () => {
      if (activeOtherSegmentationClasses.includes(segmentationClass)) {
        setActiveOtherSegmentationClasses(
          activeOtherSegmentationClasses.filter((c: string) => c !== segmentationClass)
        );
      } else {
        setActiveOtherSegmentationClasses([...activeOtherSegmentationClasses, segmentationClass]);
      }
    };

    const color = labelToColor(segmentationClass);

    return (
      <OptionContainer key={segmentationClass} onClick={handleCheckboxClick}>
        <Checkbox color={color} checked={isActive} readOnly />
        {`Show ${segmentationClass}`}
      </OptionContainer>
    );
  });

  return (
    <LabelsContainer>
      {!study?.isIceStudy && dicom?.numberOfFrames > 1 && (
        <FeatureContainer>
          <FeatureTitle>Phase</FeatureTitle>
          <FeatureOptionsContainer>{phaseLabelOptions}</FeatureOptionsContainer>
        </FeatureContainer>
      )}
      <FeatureContainer>
        <FeatureTitle>Features</FeatureTitle>
        {study?.isIceStudy ? (
          <>
            {otherSegmentationClassOptions}
            {activeSegmentationClass && (
              <FeatureDropdownContainer>
                <FeatureDropdown
                  segmentationClasses={segmentationClasses}
                  activeSegmentationClass={activeSegmentationClass}
                  segmentationReferences={segmentationReferences}
                  segmentations={segmentations}
                  activeFrame={activeFrame}
                  setActiveSegmentationClass={setActiveSegmentationClass}
                />
              </FeatureDropdownContainer>
            )}
          </>
        ) : (
          <FeatureOptionsContainer>{segmentationClassOptions}</FeatureOptionsContainer>
        )}
      </FeatureContainer>
      <FeatureContainer>
        <FeatureTitle>Visibility</FeatureTitle>
        <FeatureOptionsContainer>{visibilityOptions}</FeatureOptionsContainer>
      </FeatureContainer>
    </LabelsContainer>
  );
};

export default memo(Labels);
